import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FirestoreService } from '../firestore-service.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-progresshill',
    templateUrl: './progresshill.component.html',
    styleUrl: './progresshill.component.scss',
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class ProgresshillComponent {
  @Input() progressPoints: { x: number, y: number, progress: number, label: string, color: string }[] = [];
  @Input() isReadOnly: boolean = false
  @Output() progressHillUpdated = new EventEmitter<{ x: number, y: number, progress: number, label: string, color: string }[]>()
  pathData: string;
  circleRadius = 7
  draggingPoint: any = null;
  points: { x: number, y: number, progress: number, label: string, color: string }[] = []

  @ViewChild('bellCurve', { static: true }) bellCurve: ElementRef<SVGSVGElement>;

  constructor(private firestore: FirestoreService) {}

  ngAfterViewInit() {
    const width = 200;
    const height = 100;
    const steps = 100;
    this.points = [...this.progressPoints]

    this.pathData = this.generateBellCurvePathData(width, height, steps, this.circleRadius, this.circleRadius);
    this.points.forEach(point => {
      const x = this.progressToX(point.progress, width, this.circleRadius);
      const y = this.gaussianY(x, width, height, this.circleRadius, this.circleRadius);
      point.x = x
      point.y = y
      point.label = this.firestore.stripHTML(point.label)
      if (point.progress == undefined) {
        point.progress = 0
      }
    });
  }

  generateBellCurvePathData(usableWidth: number, usableHeight: number, steps: number, leftMargin: number, topMargin: number): string {
    let pathData = "M";
    for (let i = 0; i <= steps; i++) {
      let x = -3 + (6 * i) / steps; // x from -3 to +3
      let y = Math.exp(-0.5 * x * x); // y from 0 to 1
      let svgX = leftMargin + ((x + 3) / 6) * usableWidth;
      let svgY = topMargin + (1 - y) * usableHeight;
      if (i == 0) {
        pathData += svgX + "," + svgY;
      } else {
        pathData += " L" + svgX + "," + svgY;
      }
    }
    return pathData;
  }

  progressToX(progress: number, usableWidth: number, leftMargin: number): number { // Map progress from 0..100 to x from leftMargin..(usableWidth + leftMargin)
    return leftMargin + (progress / 100) * usableWidth;
  }

  gaussianY(x: number, usableWidth: number, usableHeight: number, leftMargin: number, topMargin: number): number {
    // Map x from leftMargin..(usableWidth + leftMargin) to x from -3 to +3
    var xVal = -3 + ((x - leftMargin) / usableWidth) * 6;
    var yVal = Math.exp(-0.5 * xVal * xVal);
    // Map y from 0..1 to (height - bottomMargin)..topMargin (SVG y is from top to bottom)
    return topMargin + (1 - yVal) * usableHeight;
  }


  updateProgressHill() {
    this.progressHillUpdated.emit([...this.points])
  }

  startDrag(event: PointerEvent, point: any) {
    if (this.isReadOnly == false) {
      event.preventDefault();
      this.draggingPoint = point;
    }
  }

  @HostListener('document:pointermove', ['$event'])
  onDrag(event: PointerEvent) {
    if (this.draggingPoint && this.isReadOnly == false) {
      event.preventDefault();
      this.updateDraggingPoint(event);
    }
  }

  @HostListener('document:pointerup', ['$event'])
  endDrag(event: PointerEvent) {
    if (this.isReadOnly == false) {
      event.preventDefault();
      this.draggingPoint = null;
      this.updateProgressHill()
    }
  }

  updateDraggingPoint(event: PointerEvent) {
    const svgElement = this.bellCurve?.nativeElement;
    if (svgElement != undefined) {
      const point = svgElement.createSVGPoint();
      if (event && event.clientX && event.clientY && svgElement != null && svgElement.getScreenCTM() != null) {
        point.x = event.clientX;
      point.y = event.clientY;
      const svgPoint = point.matrixTransform(svgElement.getScreenCTM()?.inverse());

      // Ensure the x-coordinate stays within the bounds of the bell curve
      const minX = this.circleRadius;
      const maxX = this.circleRadius + 200;
      const limitedX = Math.max(minX, Math.min(maxX, svgPoint.x));

      // Calculate the new progress based on x-coordinate
      const newProgress = ((limitedX - this.circleRadius) / 200) * 100;
      this.draggingPoint.progress = newProgress.toFixed(0);
    
        // Update the circle's position along the bell curve
        this.draggingPoint.x = this.progressToX(newProgress, 200, this.circleRadius);
        this.draggingPoint.y = this.gaussianY(
          this.draggingPoint.x,
          200,
          100,
          this.circleRadius,
          this.circleRadius
        );
      }
    }
  }
}
